import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, FullColumn, FullRow, GridItem, IconButton, InfoIcon, Row, TextButton, Tooltip, Typography, useIsInBottomSheet, useOverlayContext } from 'seagull';
import { CloseCross } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ContactPicker from '~/components/ContactsPicker/ContactPicker';
import { ManageContactModalForVendor } from '~/components/ManageContactModal';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useLocaleKeys } from '~/i18n';
import { NewContactModel } from '~/models/ContactModel';
import SignatoryCheckbox from '../../SignatoryCheckbox/SignatoryCheckbox';
import { validateRecipientClientBusiness } from '../utils/validateRecipientClientBusiness';
import { contactToReviewer, filterEligibleContacts, reviewerToContactKey } from './AdditionalRecipients.utils';
export const GetAdditionalRecipientsTID = (parentTestId)=>getTestIds(parentTestId);
export const AdditionalRecipients = (props)=>{
    const { primaryContact, reviewers, disabled = false, amendmentInfoCallout, onChange, refreshExistingContacts, showReviewerErrors, testId } = props;
    const { t: contactPickerTexts } = useLocaleKeys('agreementStudio.proposalMode.contactPicker');
    const { AdditionalRecipientsTestIds } = useMemo(()=>GetAdditionalRecipientsTID(testId), [
        testId
    ]);
    const { contacts } = useContacts();
    const queryClient = useQueryClient();
    const { showModal, pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const isInBottomSheet = useIsInBottomSheet();
    const contactsByEmail = useMemo(()=>{
        const map = new Map();
        contacts.forEach((c)=>map.set(c.email().toLowerCase(), c));
        return map;
    }, [
        contacts
    ]);
    const rowSelections = useMemo(()=>reviewers.map((r)=>{
            var _contactsByEmail_get;
            return (_contactsByEmail_get = contactsByEmail.get(reviewerToContactKey(r))) !== null && _contactsByEmail_get !== void 0 ? _contactsByEmail_get : null;
        }), [
        reviewers,
        contactsByEmail
    ]);
    const eligibleForRow = (rowIndex)=>{
        const excludeEmails = new Set(reviewers.map((r, idx)=>idx === rowIndex ? null : r.email).filter((e)=>typeof e === 'string' && e.length > 0).map((e)=>e.toLowerCase()));
        return filterEligibleContacts({
            contacts,
            primary: primaryContact,
            excludeEmails
        });
    };
    const openCreateContactModal = async ()=>{
        var _queryClient_getQueryData;
        const sharedProps = {
            testId: 'manage-contact-modal',
            origin: 'Agreement studio',
            lockedCompanyName: primaryContact.companyName(),
            lockedCompanyTooltip: contactPickerTexts.lockedCompanyTooltip()
        };
        const result = await (isInBottomSheet ? new Promise((resolve)=>{
            setTimeout(()=>{
                pushBottomSheetPage({
                    content: /*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                        onClose: (r)=>{
                            popBottomSheetPage();
                            resolve(r);
                        }
                    }))
                });
            }, 0);
        }) : showModal({
            biName: 'manage_contact_modal_for_vendor_modal',
            closeOnEscapeKey: false,
            closeOnClickOutside: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                    onClose: dismissModal
                }));
            }
        }));
        if (result == null) return undefined;
        await refreshExistingContacts();
        const refreshed = (_queryClient_getQueryData = queryClient.getQueryData([
            QueryKeys.Contacts
        ])) !== null && _queryClient_getQueryData !== void 0 ? _queryClient_getQueryData : [];
        return refreshed.find((c)=>c.id() === result.id);
    };
    const replaceRowFromContact = (rowIndex, contact)=>{
        const next = reviewers.slice();
        if (contact == null) {
            var _ref;
            var _next_rowIndex;
            next[rowIndex] = {
                firstName: '',
                lastName: '',
                email: '',
                isSignatory: (_ref = (_next_rowIndex = next[rowIndex]) === null || _next_rowIndex === void 0 ? void 0 : _next_rowIndex.isSignatory) !== null && _ref !== void 0 ? _ref : false
            };
        } else {
            var _ref1;
            var _next_rowIndex1;
            next[rowIndex] = contactToReviewer(contact, (_ref1 = (_next_rowIndex1 = next[rowIndex]) === null || _next_rowIndex1 === void 0 ? void 0 : _next_rowIndex1.isSignatory) !== null && _ref1 !== void 0 ? _ref1 : false);
        }
        onChange(next);
    };
    const toggleRequiredToSign = (rowIndex, isSignatory)=>{
        const next = reviewers.slice();
        next[rowIndex] = _object_spread_props(_object_spread({}, next[rowIndex]), {
            isSignatory
        });
        onChange(next);
    };
    const removeRow = (rowIndex)=>{
        onChange(reviewers.filter((_, idx)=>idx !== rowIndex));
    };
    const addEmptyRow = ()=>{
        onChange([
            ...reviewers,
            {
                firstName: '',
                lastName: '',
                email: '',
                isSignatory: false
            }
        ]);
    };
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 12,
        testId: AdditionalRecipientsTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(FullColumn, {
            gap: 12,
            children: [
                amendmentInfoCallout,
                reviewers.map((reviewer, index)=>{
                    var _ref, _reviewer_clientContactId, _reviewer_isSignatory;
                    const selected = rowSelections[index];
                    const eligible = eligibleForRow(index);
                    const validation = selected ? validateRecipientClientBusiness({
                        primary: primaryContact,
                        recipient: selected
                    }) : {
                        valid: true
                    };
                    const isIncomplete = showReviewerErrors === true && selected == null;
                    const showError = !validation.valid || isIncomplete;
                    const rowKey = (_ref = (_reviewer_clientContactId = reviewer.clientContactId) !== null && _reviewer_clientContactId !== void 0 ? _reviewer_clientContactId : reviewer.email) !== null && _ref !== void 0 ? _ref : "row-".concat(index);
                    return /*#__PURE__*/ _jsxs(FullColumn, {
                        gap: 4,
                        testId: AdditionalRecipientsTestIds.Row(index),
                        children: [
                            /*#__PURE__*/ _jsxs(FullRow, {
                                gap: 8,
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        style: {
                                            flex: 1,
                                            minWidth: 0
                                        },
                                        children: /*#__PURE__*/ _jsx(ContactPicker, {
                                            value: selected !== null && selected !== void 0 ? selected : NewContactModel.makeEmpty(),
                                            disabled: disabled,
                                            hideSampleContact: true,
                                            fullWidth: true,
                                            showErrorOnAnySelection: true,
                                            filterContact: (c)=>eligible.some((e)=>e.id() === c.id()),
                                            error: showError,
                                            onChange: async (change)=>{
                                                switch(change.type){
                                                    case 'initial':
                                                        replaceRowFromContact(index, null);
                                                        return;
                                                    case 'existingContact':
                                                        {
                                                            var _contacts_find;
                                                            const next = (_contacts_find = contacts.find((c)=>c.id() === change.id)) !== null && _contacts_find !== void 0 ? _contacts_find : null;
                                                            replaceRowFromContact(index, next);
                                                            return;
                                                        }
                                                    case 'newContact':
                                                        {
                                                            const created = await openCreateContactModal();
                                                            if (created != null) {
                                                                replaceRowFromContact(index, created);
                                                            }
                                                            return;
                                                        }
                                                    case 'sampleContact':
                                                        return;
                                                }
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 8,
                                        alignItems: "center",
                                        children: [
                                            /*#__PURE__*/ _jsx(SignatoryCheckbox, {
                                                checkboxTestId: AdditionalRecipientsTestIds.RowRequiredToSign(index),
                                                info: contactPickerTexts.requiredToSignTooltip(),
                                                value: (_reviewer_isSignatory = reviewer.isSignatory) !== null && _reviewer_isSignatory !== void 0 ? _reviewer_isSignatory : false,
                                                onChange: (e)=>toggleRequiredToSign(index, e.target.checked),
                                                disabled: disabled
                                            }),
                                            /*#__PURE__*/ _jsx(IconButton, {
                                                testId: AdditionalRecipientsTestIds.RowRemove(index),
                                                bi: {
                                                    name: 'remove_recipient'
                                                },
                                                disabled: disabled,
                                                tooltip: {
                                                    title: contactPickerTexts.removeRecipient()
                                                },
                                                onClick: ()=>removeRow(index),
                                                children: /*#__PURE__*/ _jsx(CloseCross, {})
                                            })
                                        ]
                                    })
                                ]
                            }),
                            !validation.valid && /*#__PURE__*/ _jsx(Typography, {
                                skin: "error",
                                variant: "paragraph2",
                                testId: AdditionalRecipientsTestIds.RowError(index),
                                children: contactPickerTexts.recipientCompanyMismatchError()
                            })
                        ]
                    }, rowKey);
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 4,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(TextButton, {
                            underline: "none",
                            testId: AdditionalRecipientsTestIds.AddButton(),
                            bi: {
                                name: 'add_additional_recipient'
                            },
                            disabled: disabled,
                            onClick: addEmptyRow,
                            children: "+ ".concat(contactPickerTexts.addAdditionalRecipientsButton())
                        }),
                        /*#__PURE__*/ _jsx(Tooltip, {
                            title: contactPickerTexts.addAdditionalRecipientsTooltip(),
                            children: /*#__PURE__*/ _jsx(Box, {
                                testId: AdditionalRecipientsTestIds.AddHelpIcon(),
                                children: /*#__PURE__*/ _jsx(InfoIcon, {
                                    info: contactPickerTexts.addAdditionalRecipientsTooltip()
                                })
                            })
                        })
                    ]
                })
            ]
        })
    });
};
